import {
    useLayoutEffect,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    Platform,
    type View
} from "react-native";
import type IHoverCardProps from "./type";
import HoverCardContent from "./components/content";
import HoverCardTrigger from "./components/trigger";
import {
    HoverCardContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    warnOnce
} from "../../utils/development";

const HoverCard: FC<IHoverCardProps> = ({
    touchBehaviour = "longPress",
    isDefaultOpen = false,
    isOpen: isOpenProp,
    align = "start",
    side = "bottom",
    customLocalize,
    onOpenChange,
    customTheme,
    closeDelay,
    isVisible,
    openDelay,
    children,
    onClose,
    onOpen
}) => {
    const {
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const baseId = useId();

    const [
        isInternalOpen,
        setIsInternalOpen
    ] = useState(isDefaultOpen);

    const [
        isSheetOpen,
        setIsSheetOpen
    ] = useState(false);

    const [
        summary,
        setSummary
    ] = useState<string | undefined>(undefined);

    const targetPressRef = useRef<(() => void) | undefined>(undefined);

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isSkipOpenCallbackRef = useRef(false);

    const isContentHoveredRef = useRef(false);

    const surfaceRef = useRef<View>(null);

    const anchorRef = useRef<View>(null);

    const controlledOpen = isOpenProp ?? isVisible;

    const isControlled = controlledOpen !== undefined;

    const isOpen = isControlled ? controlledOpen : isInternalOpen;

    const isOpenRef = useRef(isOpen);

    const isEnabled = Platform.OS === "web" && pointerType === "fine";

    const handleClearOpenTimer = useCallback(() => {
        if(openTimerRef.current === null) {
            return;
        }

        clearTimeout(openTimerRef.current);

        openTimerRef.current = null;
    }, []);

    const handleCancelClose = useCallback(() => {
        if(closeTimerRef.current === null) {
            return;
        }

        clearTimeout(closeTimerRef.current);

        closeTimerRef.current = null;
    }, []);

    const handleClose = useCallback(() => {
        isContentHoveredRef.current = false;

        handleClearOpenTimer();

        handleCancelClose();

        if(!isOpenRef.current) {
            return;
        }

        if(!isControlled) {
            isOpenRef.current = false;

            setIsInternalOpen(false);
        }

        onOpenChange?.(false);

        onClose?.();
    }, [
        handleClearOpenTimer,
        handleCancelClose,
        isControlled,
        onOpenChange,
        onClose
    ]);

    const handleOpen = useCallback(() => {
        handleClearOpenTimer();

        handleCancelClose();

        if(isOpenRef.current) {
            return;
        }

        onOpenChange?.(true);

        if(isControlled) {
            isSkipOpenCallbackRef.current = true;

            onOpen?.();

            return;
        }

        isOpenRef.current = true;

        setIsInternalOpen(true);
    }, [
        handleClearOpenTimer,
        handleCancelClose,
        isControlled,
        onOpenChange,
        onOpen
    ]);

    const handleScheduleOpen = useCallback(() => {
        handleCancelClose();

        if(isOpenRef.current || openTimerRef.current !== null) {
            return;
        }

        openTimerRef.current = setTimeout(handleOpen, openDelay ?? durations.hoverOpen);
    }, [
        handleCancelClose,
        durations.hoverOpen,
        handleOpen,
        openDelay
    ]);

    const handleScheduleClose = useCallback(() => {
        handleClearOpenTimer();

        if(!isOpenRef.current || isContentHoveredRef.current || closeTimerRef.current !== null) {
            return;
        }

        closeTimerRef.current = setTimeout(handleClose, closeDelay ?? durations.hoverClose);
    }, [
        handleClearOpenTimer,
        durations.hoverClose,
        handleClose,
        closeDelay
    ]);

    const handleContentEnter = useCallback(() => {
        isContentHoveredRef.current = true;

        handleCancelClose();
    }, [
        handleCancelClose
    ]);

    const handleContentLeave = useCallback(() => {
        isContentHoveredRef.current = false;

        handleScheduleClose();
    }, [
        handleScheduleClose
    ]);

    const handleSheetOpen = useCallback(() => {
        setIsSheetOpen(true);
    }, []);

    const handleSheetClose = useCallback(() => {
        setIsSheetOpen(false);
    }, []);

    useLayoutEffect(() => {
        isOpenRef.current = isOpen;
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        if(isSkipOpenCallbackRef.current) {
            isSkipOpenCallbackRef.current = false;

            return;
        }

        onOpen?.();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isVisible === undefined) {
            return;
        }

        warnOnce({
            message: "HoverCard: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isVisible
    ]);

    useEffect(() => {
        if(onOpen === undefined) {
            return;
        }

        warnOnce({
            message: "HoverCard: \"onOpen\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onOpen
    ]);

    useEffect(() => {
        if(onClose === undefined) {
            return;
        }

        warnOnce({
            message: "HoverCard: \"onClose\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onClose
    ]);

    useEffect(() => {
        return () => {
            handleClearOpenTimer();

            handleCancelClose();
        };
    }, [
        handleClearOpenTimer,
        handleCancelClose
    ]);

    const context = useMemo(() => ({
        contentId: `${baseId}-content`,
        summaryId: `${baseId}-summary`,
        onSummaryChange: setSummary,
        handleScheduleClose,
        handleContentEnter,
        handleContentLeave,
        handleScheduleOpen,
        handleCancelClose,
        handleSheetClose,
        handleSheetOpen,
        customLocalize,
        targetPressRef,
        touchBehaviour,
        customTheme,
        handleClose,
        isSheetOpen,
        handleOpen,
        surfaceRef,
        anchorRef,
        isEnabled,
        summary,
        isOpen,
        align,
        side
    }), [
        handleScheduleClose,
        handleScheduleOpen,
        handleContentEnter,
        handleContentLeave,
        handleCancelClose,
        handleSheetClose,
        handleSheetOpen,
        touchBehaviour,
        customLocalize,
        customTheme,
        handleClose,
        handleOpen,
        isSheetOpen,
        isEnabled,
        summary,
        baseId,
        isOpen,
        align,
        side
    ]);

    return <HoverCardContext.Provider
        value={context}
    >
        {children}
    </HoverCardContext.Provider>;
};
export default Object.assign(HoverCard, {
    Content: HoverCardContent,
    Trigger: HoverCardTrigger
});
